const OWNER_ROLE = 'Owner';

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

export function buildOwnerEmailSet(emails) {
  const set = new Set();
  (emails || []).forEach((email) => {
    const n = normalizeEmail(email);
    if (n) set.add(n);
  });
  return set;
}

export function viewerHasOwnerRole(accesscards) {
  return (accesscards || []).some((ac) => ac?.role === OWNER_ROLE);
}

function emailsInRule(rule) {
  if (!rule?.condition?.includes('emails')) return [];
  return (rule.emails || []).map(normalizeEmail).filter(Boolean);
}

export function collectOwnerEmailsFromRules(rules, ownerEmailSet) {
  const found = new Set();
  (rules || []).forEach((rule) => {
    emailsInRule(rule).forEach((email) => {
      if (ownerEmailSet.has(email)) found.add(email);
    });
  });
  return found;
}

function rulesIncludeOwnerRole(rules) {
  return (rules || []).some((rule) => {
    if (!rule?.condition?.includes('roles')) return false;
    return (rule.roles || []).includes(OWNER_ROLE);
  });
}

export function validateOwnerPermissionRules(oldRules, newRules, { viewerIsOwner, ownerEmails }) {
  if (viewerIsOwner) return { ok: true };

  const ownerEmailSet = buildOwnerEmailSet(ownerEmails);
  const old = oldRules || [];
  const next = newRules || [];

  if (rulesIncludeOwnerRole(old) !== rulesIncludeOwnerRole(next)) {
    return {
      ok: false,
      error: 'Only Owner can add or remove the Owner role from permission rules.',
    };
  }

  const oldOwnerEmails = collectOwnerEmailsFromRules(old, ownerEmailSet);
  const newOwnerEmails = collectOwnerEmailsFromRules(next, ownerEmailSet);

  if (oldOwnerEmails.size !== newOwnerEmails.size) {
    return {
      ok: false,
      error: 'Only Owner can add or remove Owner account emails from permission rules.',
    };
  }

  for (const email of oldOwnerEmails) {
    if (!newOwnerEmails.has(email)) {
      return {
        ok: false,
        error: 'Only Owner can remove Owner account emails from permission rules.',
      };
    }
  }

  for (const email of newOwnerEmails) {
    if (!oldOwnerEmails.has(email)) {
      return {
        ok: false,
        error: 'Only Owner can add Owner account emails to permission rules.',
      };
    }
  }

  return { ok: true };
}

export function guardRoleToggle(role, checked, { viewerIsOwner }) {
  if (viewerIsOwner) return { ok: true };
  if (role === OWNER_ROLE) {
    return {
      ok: false,
      error: 'Only Owner can change the Owner role in permission rules.',
    };
  }
  return { ok: true };
}

export function guardEmailsTextChange(rawText, baselineOwnerEmailsInRule, { viewerIsOwner, ownerEmails }) {
  if (viewerIsOwner) return { ok: true, value: rawText };

  const ownerEmailSet = buildOwnerEmailSet(ownerEmails);
  const baseline = new Set(
    (baselineOwnerEmailsInRule || [])
      .map(normalizeEmail)
      .filter((email) => ownerEmailSet.has(email))
  );
  const parsed = (rawText || '')
    .split(/[\n,;]+/)
    .map((x) => normalizeEmail(x))
    .filter(Boolean);
  const parsedSet = new Set(parsed);

  for (const email of baseline) {
    if (!parsedSet.has(email)) {
      return {
        ok: false,
        error: 'Only Owner can remove Owner account emails from permission rules.',
      };
    }
  }

  for (const email of parsedSet) {
    if (ownerEmailSet.has(email) && !baseline.has(email)) {
      return {
        ok: false,
        error: 'Only Owner can add Owner account emails to permission rules.',
      };
    }
  }

  return { ok: true, value: rawText };
}

export function baselineOwnerEmailsForRule(rule, ownerEmails) {
  const ownerEmailSet = buildOwnerEmailSet(ownerEmails);
  return (rule?.emails || []).filter((email) => ownerEmailSet.has(normalizeEmail(email)));
}
